import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { Work, WorkAvailable } from '../../models/work';
import { ApiService } from '../../services/api.service';
import { UiNotificationService } from '../../services/ui-notification.service';
import { TabsModule } from 'primeng/tabs';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';
import { StatusDatePipe } from '../../utilities/date-pipes';
import { ListboxModule } from 'primeng/listbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { DividerModule } from 'primeng/divider';
import { Reset } from '../../utilities/reset';

@Component({
  selector: 'app-work-dialog',
  imports: [
    FormsModule,
    CommonModule,
    DialogModule,
    SelectModule,
    TranslatePipe,
    TabsModule,
    DatePickerModule,
    StatusDatePipe,
    ToggleSwitchModule,
    ButtonModule,
    ListboxModule,
    InputNumberModule,
    DividerModule
  ],
  standalone: true,
  templateUrl: './work-dialog.component.html',
  styles: ``,
})
export class WorkDialogComponent implements OnInit {
  @Input() isNew = false;
  @Input() messageService!: UiNotificationService;

  // visibilità dialog (bidirezionale)
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  // utente
  @Input() work: Work = Reset.work();

  // evento finale
  @Output() saved = new EventEmitter<Work>();

  typeOptions: WorkAvailable[] = [];
  selectedType: WorkAvailable = { typeId: '', description: '' };

  orgOptions: { id: number; value: string }[] = [];
  selectedOrg: { id: number; value: string } = { id: 0, value: '' };

  deptsOptions: { id: number; value: string }[] = [];
  selectedDepts: { id: number; value: string }[] = [];

  tabsId: string = '0';

  delta: Date = new Date(2000, 1, 1, 0, 0, 0, 0);

  titleDays: string = 'days';
  titleTime: string = 'repeatAt';

  constructor(private apiService: ApiService, private cdr: ChangeDetectorRef) {

  }

  ngOnChanges() {
    this.tabsId = '0';
    this.selectedOrg = { id: 0, value: '' };
    this.selectedDepts = [];
    this.selectedType = { typeId: '', description: '' };

    if (this.work && this.visible && !this.isNew) {
      const [h, m, s] = this.work.deltaTime.split(':').map(Number);
      this.delta = new Date(2000, 1, 1, h, m, s, 0);

      let data = this.work.id.split("$").filter(e => e.trim() !== "");
      if (data.length > 0) {
        this.selectedType = this.typeOptions.find(o => data[0] == o.typeId) || { typeId: '', description: '' }
      }

      if (data.length > 1) {
        let params = data[1].split("|").filter(e => e.trim() !== "");
        if (params.length > 0) {
          this.selectedOrg = this.orgOptions.find(o => parseInt(params[0]) == o.id) || { id: 0, value: '' }

          if (params.length > 1) this.onOrgChange(params[1]);
        }
      }

      this.onDaysInput(undefined);
    }
  }

  ngOnInit(): void {
    this.tabsId = '0';

    this.apiService.getWorkAvailable().subscribe({
      next: (data) => {
        this.typeOptions = structuredClone(data);
      },
      error: (err: HttpErrorResponse) => {
        this.messageService.showError(err);
      }
    })

    this.apiService.getOrganizations().subscribe({
      next: (data) => {
        this.orgOptions = data.map(org => ({
          id: org.organizationId,
          value: org.name
        }));
      },
      error: (err: HttpErrorResponse) => {
        this.messageService.showError(err);
      }
    })
  }

  onClose() {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onDaysInput(event: any) {
    if (this.work.deltaDays > 0) {
      this.titleDays = 'days';
      this.titleTime = 'executeAt';
    } else {
      this.titleDays = 'days';
      this.titleTime = 'repeatAt';
    }
  }

  onDeptChange(event: any) {
    if (this.selectedDepts.length > 0) {
      this.work.departmentIds = this.selectedDepts.map(d => d.id);
      this.work.departments = this.selectedDepts.map(d => d.value);
    }
  }

  onOrgChange(event: any) {
    let depts: number[] = [];
    if (typeof event === 'string') {
      depts = event.split("-").filter(e => e.trim() !== "").map(e => Number(e));
    }
    else if (this.selectedOrg.id !== event.value.id) {
      this.selectedOrg = event.value;
    }

    this.work.organizationId = this.selectedOrg.id;
    this.work.organization = this.selectedOrg.value;

    this.apiService.getOrganizationDepartments(this.selectedOrg.id).subscribe({
      next: (data) => {
        this.deptsOptions = data.map(dept => ({
          id: dept.id,
          value: dept.name,
        }));

        this.selectedDepts = [];
        if (depts.length > 0) {
          this.selectedDepts = this.deptsOptions.filter(d => depts.includes(d.id));
        }

        this.cdr.detectChanges();
      },
      error: (err: HttpErrorResponse) => {
        this.messageService.showError(err);
      }
    })
  }

  onSave() {
    if (this.selectedType.typeId === '') {
      this.messageService.showErrorMessage('messages.selectWorker');
      return;
    }

    if (this.work.all) {
      this.work.organizationId = 0
      this.work.organization = ''
      this.work.departmentIds = []
      this.work.departments = []
    } else {
      if (this.work.organizationId == 0) {
        this.messageService.showErrorMessage('messages.referenceOrg');
        this.tabsId = '1';
        return;
      }

      if (this.work.departmentIds.length == 0) {
        this.messageService.showErrorMessage('messages.referenceDept');
        this.tabsId = '1';
        return;
      }
    }

    this.work.id = this.selectedType.typeId;

    const hh = this.delta.getHours().toString().padStart(2, '0');
    const mm = this.delta.getMinutes().toString().padStart(2, '0');
    const ss = this.delta.getSeconds().toString().padStart(2, '0');
    this.work.deltaTime = `${hh}:${mm}:${ss}`;

    if (this.isNew) {
       this.apiService.addWork(this.work).subscribe({
         next: () => {
           this.savedCompleted();
           this.messageService.showSuccess('messages.dataSaved');
         },
         error: (err: HttpErrorResponse) => {
           this.messageService.showError(err);
         }
       });
    } else {
       this.apiService.updateWork(this.work).subscribe({
         next: () => {
           this.savedCompleted();
           this.messageService.showSuccess('messages.dataSaved');
         },
         error: (err: HttpErrorResponse) => {
           this.messageService.showError(err);
         }
       });
    }
  }

  private savedCompleted() {
    this.saved.emit(this.work);
    this.onClose();
  }
}
